/**
 * Fields Yaruta filled from what the family taught by correcting earlier
 * items (Phase 3b, YAR-345) — a place for an activity, the end of a weekly
 * series. The extraction job writes the value into the review item and
 * records it under `raw_extraction._memory_fill`; this tells the Review card
 * and the item form when to show a muted "From your past edits" caption.
 * A time of day the model read from a vague phrase the family taught ("after
 * school" = 15:15) is flagged the same way (`time`, as "HH:MM"), so the
 * parent can see where it came from and correct it.
 *
 * The caption shows only while the field still holds the filled value: once
 * the parent changes it, it is their value, not Yaruta's.
 */

export const MEMORY_FILL_CAPTION = 'From your past edits';
/** The same caption, run on after a summary line ("… — from your past edits"). */
export const MEMORY_FILL_SUFFIX = 'from your past edits';

export type MemoryFillField = 'location' | 'recurrence_until' | 'time';

function asObject(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function norm(v: unknown): string {
  return typeof v === 'string'
    ? v.trim().replace(/\s+/g, ' ').toLowerCase()
    : '';
}

/** The value Yaruta filled for `field`, or null when the field was not filled. */
export function memoryFilledValue(
  rawExtraction: unknown,
  field: MemoryFillField,
): string | null {
  const raw = asObject(rawExtraction);
  const fill = asObject(raw?._memory_fill);
  if (!raw || !fill || !asObject(fill[field])) return null;
  if (field === 'time') {
    const model = asObject(fill.time)?.model;
    return typeof model === 'string' && /^\d{2}:\d{2}$/.test(model)
      ? model
      : null;
  }
  if (field === 'location') {
    return typeof raw.location === 'string' && raw.location.trim()
      ? raw.location
      : null;
  }
  const until = asObject(raw.recurrence)?.until;
  return typeof until === 'string' && until ? until : null;
}

/** True when `current` is still the value Yaruta filled for `field`. */
export function isMemoryFilled(
  rawExtraction: unknown,
  field: MemoryFillField,
  current: unknown,
): boolean {
  const filled = memoryFilledValue(rawExtraction, field);
  return (
    filled !== null && norm(filled) === norm(current) && norm(current) !== ''
  );
}

/**
 * A child Yaruta matched from the email's own words for an item the model
 * left without one (eval-gates round 2) — "Summer B. from Mrs. Diaz's 4th
 * grade" → Summer Brooks. The extraction job records it under
 * `raw_extraction._memory_fill.children` with the words that matched and the
 * child's name. Returns the caption while the item still carries exactly
 * that child, else null: once the parent changes the children, they are the
 * parent's choice, not Yaruta's.
 */
export function childMentionCaption(
  rawExtraction: unknown,
  currentNames: readonly string[],
): string | null {
  const fill = asObject(
    asObject(asObject(rawExtraction)?._memory_fill)?.children,
  );
  if (!fill) return null;
  const words = Array.isArray(fill.words) ? fill.words : [];
  const names = Array.isArray(fill.names) ? fill.names : [];
  const mention = typeof words[0] === 'string' ? words[0].trim() : '';
  if (!mention || names.length === 0) return null;
  const want = new Set(names.map(norm).filter(Boolean));
  const have = new Set(currentNames.map(norm).filter(Boolean));
  if (want.size === 0 || want.size !== have.size) return null;
  for (const n of want) if (!have.has(n)) return null;
  return `Matched from \u201c${mention}\u201d in the email`;
}
